import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleLabel } from '../../lib/roles';
import { unwrap } from '../../lib/api-envelope';
import { shopGapMessage, isJobPayExemptGap } from '../../constants/shop-pay';
import { formatCalendarDate, todayCalendarDate, zonedParts } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency, fmtHours, parseMoney } from '../../lib/display-labels';
import { reportHoursText } from '../../lib/shift-hours';
import { sortCompare } from '../../components/SortTh';
import { useVerticalLabel } from '../../hooks/useVerticalCatalog';
import {
  ChevronDown,
  Download,
  Loader2,
  Clock,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Warehouse,
  Info,
} from 'lucide-react';

/* ── Types ── */
interface PayPeriod {
  id: string;
  period_number?: number;
  period_start: string;
  period_end: string;
  status: string;
}

/* ── Helpers ── */
/**
 * period_start/period_end are calendar days. The `+ 'T00:00:00'` splice was the
 * right idea and the right output; it just had no guard, so an absent period
 * bound rendered the string "Invalid Date". @see formatCalendarDate.
 */
const formatDate = (iso: string | null | undefined): string =>
  formatCalendarDate(iso, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';

const formatDateShort = (iso: string | null | undefined): string =>
  formatCalendarDate(iso, { month: 'short', day: 'numeric' }) ?? '—';

/**
 * Payroll hours and money render from the shared helpers, which reject absence
 * BEFORE coercing. The local copies did `(Number(x) || 0)`, so a row the server
 * could not score showed 0.0 hours and $0.00 gross on the screen that decides
 * what a hand is paid. One decimal is kept for hours — these tables were built
 * on it — via fmtHours' `digits` argument.
 */
const formatHours = (hrs: number | string | null | undefined): string => fmtHours(hrs, 1);

export type TimesheetReportTab = 'hours' | 'ot' | 'threshold' | 'cost' | 'shop';
type TabKey = TimesheetReportTab;

/**
 * `hidden` keeps a tab a valid `?report=` key — the link still opens it — while
 * leaving it out of the tab strip.
 *
 * Cost by job is hidden: it totals timesheets.gross_pay, which nothing has
 * written since api#343, and job_name / well_legal_id are NULL on every row, so
 * each period collapses to one "No Job" row. Field hands are paid a percentage
 * of the ticket on jobs, not by the clock, so a per-timesheet dollar figure is
 * the wrong shape for that work. The component and its endpoint are kept.
 */
export const TIMESHEET_REPORT_TABS: { key: TabKey; label: string; hidden?: boolean }[] = [
  { key: 'hours', label: 'Hours breakdown' },
  { key: 'ot', label: 'OT analysis' },
  { key: 'threshold', label: 'OT threshold' },
  { key: 'cost', label: 'Cost by job', hidden: true },
  { key: 'shop', label: 'Shop time' },
];

/**
 * Who GET /pay-periods/:id/shop-preview lets through, spelled literally.
 *
 * Mirrors `requireRole('admin', 'office_staff', 'dispatcher')` on the route as of
 * fieldiq-api #395. Before that it was admin | office_staff and NON-strict, so a
 * dispatcher passed only on a tenant with `dispatcher_admin_access` on — which
 * made "can dispatch see what shop time costs" a per-tenant accident. This file
 * used to spend a GET /settings/roles query working out the answer; #395 removed
 * the question, so the query is gone with it.
 *
 * `supervisor` is absent on purpose and matches the server: nothing on the payroll
 * router grants a supervisor a pay-rate door, so adding one here would be a new
 * grant rather than the removal of an inconsistency.
 */
const SHOP_PREVIEW_ROLES = ['admin', 'office_staff', 'dispatcher'];

/** One shop worker's approved hours for a period, and what they would pay. */
interface ShopPreviewRow {
  userId: string;
  workerName: string;
  hours: number | null;
  shiftCount: number | null;
  /** Dollars per hour, or null. The API never sends 0 as a stand-in. */
  hourlyRate: number | null;
  /**
   * The worker's Shop-row overtime switch (API 0384). null when there is no
   * rate to carry it — a dash, which is a different fact from "No".
   */
  overtimeEligible?: boolean | null;
  /** hours x rate, or null when the rate is unknown. Never 0 as a stand-in. */
  amount: number | null;
  gap: string | null;
  gapMessage: string | null;
  /**
   * Where and how the hours were priced (fieldiq-api #1110). Since then a row is
   * one worker × one (rateSource, workLocation, provinceState, customerName), so
   * a US hourly hand with field AND shop time has two rows with one `userId`.
   * This tab shows `workLocation === 'shop'` rows only — field hours are not
   * shop time — and keys rows by `shopRowKey`, never by `userId` alone.
   */
  rateSource?: 'shop' | 'hourly' | null;
  workLocation?: 'shop' | 'field' | null;
  provinceState?: string | null;
  customerName?: string | null;
}

/** A row's identity: a worker can hold more than one row (see `workLocation`). */
export function shopRowKey(r: Pick<ShopPreviewRow, 'userId' | 'workLocation' | 'provinceState' | 'customerName'>): string {
  return [r.userId, r.workLocation ?? '', r.provinceState ?? '', r.customerName ?? ''].join('|');
}

/** Yes / No / blank-or-dash. Only a literal true is "Yes"; null is unknown. */
function shopOtLabel(v: boolean | null | undefined, absent: string): string {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return absent;
}

/**
 * The figures this tab shows, computed by the API over `workLocation === 'shop'`
 * rows only, workers counted DISTINCT (fieldiq-api #1118). On a tenant with no
 * hourly workers — every Canadian one — this deep-equals the top-level figures.
 *
 * `workersPriced` is NOT `workerCount − workersMissingRate − workersJobPayExempt`:
 * a worker with one priced shift and one unpriced one is not priced, and is in
 * neither subtracted count. This tab used to compute exactly that subtraction in
 * the browser; it reads the API's figure instead.
 */
interface ShopSummary {
  totalHours: number | null;
  pricedAmount: number | null;
  unpricedHours: number | null;
  workersMissingRate: number | null;
  workersJobPayExempt: number | null;
  workerCount: number | null;
  workersPriced: number | null;
}

interface ShopPreview {
  rows: ShopPreviewRow[];
  /** Shop rows only. Absent on an API older than #1118: every figure then reads "—". */
  shop?: ShopSummary | null;
  totalHours: number | null;
  /** Sum of the priceable rows ONLY. Unpriced hours are excluded, not zeroed. */
  pricedAmount: number | null;
  /** Hours with no rate, and therefore NOT inside pricedAmount. */
  unpricedHours: number | null;
  /**
   * Workers whose hours could not be priced because a rate is MISSING.
   *
   * As of fieldiq-api #423 this EXCLUDES job-pay-exempt workers — the server
   * keys it on the gap reason rather than on `hourlyRate === null`. These
   * top-level figures cover ALL rows, field included; the tab reads `shop`.
   */
  workersMissingRate: number | null;
  /**
   * Workers left out of `workersMissingRate` because they are deliberately not
   * paid from jobs. Reported beside it rather than folded in or dropped: their
   * hours are real and are still inside `totalHours`.
   */
  workersJobPayExempt: number | null;
}

/** The requireRole refusal: a bare `{ error }`, no `success`, no `code`. */
function isRefusal(err: unknown): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === 403;
}

/**
 * ── THE PAYLOAD KEYS, AS payroll-reports.routes.ts ACTUALLY NAMES THEM ──────
 * This screen read `overtime_hours`, `double_time_hours`, `vertical`, `well_id`,
 * `total_cost`, and an OT response shaped `{ summary, byVertical }`. The routes
 * return `ot_hours`, `dt_hours`, `primary_vertical`, `well_legal_id`,
 * `total_labour_cost`, and the OT breakdown as a bare array. Every one of those
 * reads found nothing, so OT and double-time rendered as dashes on every row
 * and the OT tab said "No OT data" for every period — plausible, and wrong.
 * Verified against the live response, not assumed. The old names are kept as
 * fallbacks so a response in either spelling still renders.
 */
const rowOtHours = (r: any) => r.ot_hours ?? r.overtime_hours;
const rowDtHours = (r: any) => r.dt_hours ?? r.double_time_hours;
const rowVerticalKey = (r: any): string | null => r.primary_vertical ?? r.vertical ?? null;
const rowWell = (r: any) => r.well_legal_id ?? r.well_id;
const rowLabourCost = (r: any) => r.total_labour_cost ?? r.total_cost;

/* ── Shared table classes ──
 *
 * THE HEADER CELLS CARRY NOTHING BUT THEIR WORD. No sort arrow, no icon, no
 * screen-reader-only span: `TimesheetReports.payload-keys.test.tsx` asserts the
 * raw `.textContent` of every column header against an exact array, so a glyph
 * or a stray whitespace-producing node inside a header cell fails it — and that
 * test is the one pinning the payload-key fix, which is worth more than a sort
 * control this table has never had.
 */
const TH_CLASS = 'px-4 py-3';
const THEAD_CLASS =
  'border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted text-left whitespace-nowrap';
const TD_CLASS = 'px-4 py-3 text-sm text-fieldiq-text tabular-nums';

/** The one panel shape every report body sits in. */
const PANEL_CLASS = 'overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card';

/**
 * A row's 4px status rail. Transparent where the report has no status to
 * report — which is most of them: an hours or cost row is an aggregate, not a
 * record with a state, and a coloured rail on one would invent a status. The
 * heading row carries the same transparent width so the columns line up.
 */
const RAIL_NONE = 'border-l-4 border-l-transparent';

/* ── CSV export helper ── */
function downloadCSV(headers: string[], rows: string[][], filename: string) {
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** The amber Export CSV button, identical on the two tabs that offer one. */
function ExportCsvButton({ onClick, label = 'Export CSV' }: { onClick: () => void; label?: string }) {
  return (
    <button onClick={onClick} className="fi-action fi-action--compact fi-action--primary">
      <Download size={14} />
      {label}
    </button>
  );
}

/** An empty report: one glyph, one sentence, in the panel the rows would fill. */
function EmptyReport({ message }: { message: string }) {
  return (
    <div className="px-4 py-16 text-center">
      <Clock size={40} className="mx-auto mb-3 text-fieldiq-muted" />
      <p className="text-base text-fieldiq-muted">{message}</p>
    </div>
  );
}

/**
 * One figure from a report, as a card with an accessible name.
 *
 * `role="group"` plus `aria-label={label}` give the card's label and its value
 * ONE addressable identity — for a screen reader, and for
 * `getByRole('group', { name })` instead of walking up from the label text to a
 * parent node. The value must stay a DESCENDANT of the labelled element; the
 * suites read it with `toHaveTextContent`.
 */
function ReportStatCard({ label, value, caption }: { label: string; value: string; caption?: string | null }) {
  return (
    <div role="group" aria-label={label} className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
      <div className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{label}</div>
      <div className="mt-1.5 text-2xl font-extrabold tabular-nums text-fieldiq-text">{value}</div>
      {caption && <div className="mt-1 text-xs text-fieldiq-muted">{caption}</div>}
    </div>
  );
}

/**
 * Rendered where the shop rows would be, for a caller the server will refuse.
 *
 * THREE STATES, NEVER TWO. "You may not see this" is a different fact from
 * "there is nothing here", and collapsing them is what shipped once already on
 * the pay-profile screens: a 403 fell into an empty state, and the empty state
 * then invited the caller to create the record they simply could not see. So a
 * refusal says it is a refusal, names the role, and offers nothing to click.
 *
 * ── STILL REACHED, AFTER #395 WIDENED THE ROUTE TO DISPATCH ─────────────────
 * Checked rather than assumed, because a branch nobody hits should be deleted
 * rather than maintained. Of the seven tenant roles, `field_worker` / `driver`
 * render the field tree and `customer` renders the portal tree (AuthContext), so
 * neither can reach this screen at all. The office tree is reached by admin,
 * office_staff, dispatcher, supervisor and logistics — and the last two are
 * refused. So this notice is live, for two real office-side roles.
 *
 * `supervisor` is the one that matters: they approve the very timesheets this
 * report reads, so it is exactly the role that will try, and exactly the role
 * for which a silent empty report would be misread as "no shop work happened".
 *
 * THE SENTENCE IS ONE TEXT RUN, and has to stay one. The suites match it with
 * `getByText(/permission to view shop pay/i)`, which reads an element's own
 * text nodes — wrapping half of it in an emphasis span to style the role would
 * split the run and the query would find nothing.
 */
/**
 * The same three-states rule as NoShopPayAccessNotice below, for the other four
 * tabs — which until API #408 had no error branch at all and rendered a refusal
 * as an empty report.
 *
 * Deliberately a SEPARATE notice rather than a reuse of the shop one: the two
 * gates differ (this is admin/office_staff non-strict, shop is
 * admin/office_staff/dispatcher), so one sentence cannot be true of both. Naming
 * the wrong set of roles is the failure this whole family is about.
 */
function NoPayReportAccessNotice({ role }: { role?: string }) {
  return (
    <div className={`${PANEL_CLASS} px-6 py-10 text-center`}>
      <AlertCircle size={40} className="mx-auto mb-3 text-fieldiq-muted" />
      <div className="mb-1.5 text-base font-semibold text-fieldiq-text">
        You don&rsquo;t have permission to view timesheet reports
      </div>
      <div className="mx-auto max-w-[560px] text-sm leading-relaxed text-fieldiq-muted">
        Hours, overtime and what work cost are limited to admins and office staff
        {role ? ` — yours is ${getRoleLabel(role)}` : ''}. This is not a statement about
        whether anyone worked this period; it is a statement about what you can see.
      </div>
    </div>
  );
}

function NoShopPayAccessNotice({ role }: { role?: string }) {
  return (
    <div className={`${PANEL_CLASS} px-6 py-10 text-center`}>
      <AlertCircle size={40} className="mx-auto mb-3 text-fieldiq-muted" />
      <div className="mb-1.5 text-base font-semibold text-fieldiq-text">
        You don&rsquo;t have permission to view shop pay
      </div>
      <div className="mx-auto max-w-[560px] text-sm leading-relaxed text-fieldiq-muted">
        Shop hours and what they cost are limited to admins, office staff and dispatch
        {role ? ` — yours is ${getRoleLabel(role)}` : ''}. This is not a statement about
        whether anyone worked in the shop this period; it is a statement about what you
        can see.
      </div>
    </div>
  );
}

/**
 * The OT-threshold verdict, as the house status badge.
 *
 * AN ABSENT `hours_remaining` GETS A SLATE DASH, NOT "Over threshold". The
 * chain this replaces was `remaining > 20 ? OK : remaining > 0 ? Near limit :
 * Over threshold`, and `null > 0` is false — so a worker the server could not
 * score was badged **Over threshold**, in red, on the report that decides
 * whether a hand may legally keep working. That is the fe#710 shape exactly: a
 * fallback to a real status on a register whose whole claim is the status. The
 * hours cell beside it already renders the absence as a dash, so the two now
 * agree instead of contradicting each other.
 *
 * Three states, three hues, no sharing needed.
 */
function thresholdVerdict(remaining: unknown): { label: string; fill: string; rail: string } {
  const n = parseMoney(remaining);
  if (n === null) return { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
  if (n > 20) return { label: 'OK', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' };
  if (n > 0) return { label: 'Near limit', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' };
  return { label: 'Over threshold', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' };
}

/** The saturated signal badge: dark ink on the fill, uppercase, never a pale wash. */
function ReportBadge({ label, fill }: { label: string; fill: string }) {
  return (
    <span
      className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${fill}`}
    >
      {label}
    </span>
  );
}

/* ── Component ── */
/**
 * The report half of the Timesheets screen (ApprovalQueueScreen). This was its own
 * page at /payroll/reports, titled "Payroll Reports" — the wrong object: every tab
 * is an aggregate over the `timesheets` clock rows, and none of them reads the
 * payroll ledger (employee_timesheets / timesheet_entries). So it now sits beside
 * the list it summarises, and the old route redirects here.
 *
 * THE PERIOD PICKER IS THIS COMPONENT'S STATE, NOT THE SCREEN'S. The status list
 * is scoped by status and never by pay period; keeping the picker in here is what
 * makes "it changes the reports and not the list" true by construction rather
 * than by two components agreeing to ignore it.
 *
 * The tab is controlled by the parent when `tab` is passed, so the screen can keep
 * it in the URL and the old route can deep-link to a tab.
 *
 * ── IT NO LONGER PAINTS ITS OWN DARK PALETTE ────────────────────────────────
 * This file carried a literal copy of the host screen's hardcoded hex palette,
 * with a docblock saying that matching the host was what kept both halves
 * legible and that tokenising was "a separate sweep over both files". This is
 * that sweep. Every surface, edge and ink is now a `--fieldiq-*` token and every
 * accent a `--fi-signal-*` one, so the reports follow the light/dark switch with
 * the rest of the app instead of staying navy inside a white page.
 */
export default function TimesheetReports({
  tab,
  onTabChange,
}: {
  tab?: TabKey;
  onTabChange?: (tab: TabKey) => void;
} = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const verticalLabel = useVerticalLabel();
  const rowVertical = (r: any): string | null => {
    const v = rowVerticalKey(r);
    return v ? verticalLabel(v) : null;
  };
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [ownTab, setOwnTab] = useState<TabKey>('hours');
  const activeTab = tab ?? ownTab;
  const setActiveTab = (t: TabKey) => {
    setOwnTab(t);
    onTabChange?.(t);
  };
  // "This month" is the TENANT's (companies.time_zone), not the device's. With
  // no zone there is no honest month: the pickers start empty and nothing is
  // fetched until one is chosen.
  const timeZone = useTenantTimeZone();
  const tenantNow = zonedParts(new Date(), timeZone);
  const [thresholdMonth, setThresholdMonth] = useState<number | null>(tenantNow?.month ?? null);
  const [thresholdYear, setThresholdYear] = useState<number | null>(tenantNow?.year ?? null);

  // Fetch pay periods
  const { data: periodsData, isLoading: periodsLoading } = useQuery({
    queryKey: ['pay-periods-list'],
    queryFn: () => api.payPeriods.list({ limit: 50, offset: 0 }).then((r) => r.data?.data ?? r.data ?? []),
  });

  const periods: PayPeriod[] = useMemo(() => {
    const raw = Array.isArray(periodsData) ? periodsData : [];
    return [...raw].sort((a, b) => b.period_start.localeCompare(a.period_start));
  }, [periodsData]);

  // "The period containing today" — the TENANT's today. No zone, no match.
  const defaultPeriodId = useMemo(() => {
    if (periods.length === 0) return null;
    const today = todayCalendarDate(timeZone);
    const current = today
      ? periods.find((p) => p.period_start <= today && today <= p.period_end)
      : undefined;
    if (current) return current.id;
    const withData = periods.find((p) => p.status !== 'empty');
    if (withData) return withData.id;
    return periods[0].id;
  }, [periods, timeZone]);

  const activePeriodId = selectedPeriodId ?? defaultPeriodId;
  const activePeriod = periods.find((p) => p.id === activePeriodId);

  // Tab data queries
  const { data: hoursSummaryData, isLoading: hoursLoading, error: hoursError } = useQuery({
    queryKey: ['payroll-reports-hours', activePeriodId],
    // The whole body, not `.data`: the totals (api#825) ride beside the rows
    // at the top level, and reading `.data` alone would throw them away.
    queryFn: () => api.payrollReports.hoursSummary(activePeriodId!).then((r) => r.data ?? []),
    enabled: !!activePeriodId && activeTab === 'hours',
    // A 403 is an answer, not a flake — same reasoning as the shop query.
    retry: false,
  });

  const { data: otBreakdownData, isLoading: otLoading, error: otError } = useQuery({
    queryKey: ['payroll-reports-ot', activePeriodId],
    queryFn: () => api.payrollReports.otBreakdown(activePeriodId!).then((r) => r.data?.data ?? r.data ?? {}),
    enabled: !!activePeriodId && activeTab === 'ot',
    // A 403 is an answer, not a flake — same reasoning as the shop query.
    retry: false,
  });

  const { data: otThresholdData, isLoading: thresholdLoading, error: thresholdError } = useQuery({
    queryKey: ['payroll-reports-threshold', thresholdYear, thresholdMonth],
    // The whole body, as for hours: the totals (api#825) ride beside the rows.
    queryFn: () => api.payrollReports.otThresholdStatus(thresholdYear!, thresholdMonth!).then((r) => r.data ?? []),
    enabled: activeTab === 'threshold' && thresholdYear != null && thresholdMonth != null,
    // A 403 is an answer, not a flake — same reasoning as the shop query.
    retry: false,
  });

  const { data: costByJobData, isLoading: costLoading, error: costError } = useQuery({
    queryKey: ['payroll-reports-cost', activePeriodId],
    // The whole body, as for hours: the totals (api#825) ride beside the rows.
    queryFn: () => api.payrollReports.costByJob(activePeriodId!).then((r) => r.data ?? []),
    enabled: !!activePeriodId && activeTab === 'cost',
    // A 403 is an answer, not a flake — same reasoning as the shop query.
    retry: false,
  });

  /**
   * WHETHER THE SERVER REFUSED THESE FOUR — READ, NOT PREDICTED.
   *
   * These four tabs are one gate: requireRole('admin','office_staff') on all
   * five routes in payroll-reports.routes.ts (API #408). Before that gate they
   * had no error branch at all, so a 403 fell through `?? []` / `?? {}` into an
   * empty report — the same failure this file already fixed once for shop pay.
   *
   * WHY THIS DOES NOT USE A LITERAL ALLOW LIST LIKE `canViewShopPay` BELOW.
   * That gate is STRICT in effect — #395 made dispatch a first-class reader, so
   * `dispatcher_admin_access` has no bearing and the role alone decides. This
   * one is NON-strict: an elevated dispatcher passes, and a literal
   * ['admin','office_staff'] would show a refusal notice to someone the server
   * would have served. Dash Energy has that flag ON today, so that is a live
   * tenant's dispatcher, not a hypothetical. Reading the server's answer cannot
   * be wrong about the server's answer.
   *
   * The cost is one refused round-trip per tab, which `retry: false` bounds.
   */
  const reportsForbidden =
    (activeTab === 'hours' && isRefusal(hoursError)) ||
    (activeTab === 'ot' && isRefusal(otError)) ||
    (activeTab === 'threshold' && isRefusal(thresholdError)) ||
    (activeTab === 'cost' && isRefusal(costError));

  /**
   * A literal allow list, matching the route exactly — no settings lookup, and
   * nothing to wait for.
   *
   * This DIVERGES from ApprovalQueueScreen, which still queries GET /settings/roles
   * for `dispatcher_admin_access`, and the two should NOT be converged. Approval
   * is still gated by a non-strict `requireRole` where elevation is the only way a
   * dispatcher passes, and on a tenant whose eligible approvers are one admin plus
   * one elevated dispatcher, under-granting there would leave nobody able to
   * approve. Shop-preview no longer has that shape: #395 made dispatch a first-
   * class reader, so the flag has no bearing here and querying it would be a
   * request whose answer cannot change the outcome.
   */
  const canViewShopPay = useMemo(() => {
    const role = user?.role;
    return !!role && SHOP_PREVIEW_ROLES.includes(role);
  }, [user?.role]);

  const {
    data: shopPreviewData,
    isLoading: shopLoading,
    error: shopError,
  } = useQuery({
    queryKey: ['payroll-reports-shop', activePeriodId],
    // unwrap, not `r.data?.data ?? r.data`: that fallthrough hands back the
    // ENVELOPE when the payload is absent, and every field then reads undefined
    // — which here would render as a period with no shop hours.
    queryFn: () => api.payroll.shopPreview(activePeriodId!).then((r) => unwrap<ShopPreview>(r.data)),
    enabled: !!activePeriodId && activeTab === 'shop' && canViewShopPay,
    // A 403 is an answer, not a flake. Retrying it delays the notice and tells
    // the caller nothing new.
    retry: false,
  });

  const periodLabel = (p: PayPeriod) =>
    `Period #${p.period_number ?? '—'} — ${formatDateShort(p.period_start)}–${formatDate(p.period_end)}`;

  const isTabLoading =
    (activeTab === 'hours' && hoursLoading) ||
    (activeTab === 'ot' && otLoading) ||
    (activeTab === 'threshold' && thresholdLoading) ||
    (activeTab === 'cost' && costLoading) ||
    (activeTab === 'shop' && canViewShopPay && shopLoading);

  // Hours summary data
  const hoursRows: any[] = useMemo(() => {
    const body: any = hoursSummaryData;
    if (Array.isArray(body)) return body;
    return Array.isArray(body?.data) ? body.data : [];
  }, [hoursSummaryData]);
  // THE TOTALS ROW IS THE API'S (api#825), rendered as sent — never summed
  // here. The route computes each total in the same statement as its rows, so
  // it matches them to the cent even with clock rounding on; a browser sum of
  // the rounded per-worker figures is not guaranteed to. An API that sends no
  // `totals` gets a row of dashes, not a row added up from the cells above.
  const hoursTotals: any = (!Array.isArray(hoursSummaryData) && (hoursSummaryData as any)?.totals) || null;

  // OT breakdown data
  // The route returns one row per vertical and NO summary object. The cards render
  // only if a summary is sent; otherwise they are omitted, not summed — a
  // client-side total is a second answer, and "workers with OT" is not even
  // derivable: worker_count counts every worker with a timesheet in the
  // vertical, OT or not.
  const otData: any = otBreakdownData && typeof otBreakdownData === 'object' ? otBreakdownData : {};
  const otSummary: any = (!Array.isArray(otData) && otData.summary) || null;
  const otRows: any[] = Array.isArray(otData)
    ? otData
    : Array.isArray(otData.byVertical ?? otData.breakdown) ? (otData.byVertical ?? otData.breakdown) : [];

  // OT threshold data. Totals are the API's, like the hours tab's — never summed here.
  const thresholdRows: any[] = useMemo(() => {
    const body: any = otThresholdData;
    if (Array.isArray(body)) return body;
    return Array.isArray(body?.data) ? body.data : [];
  }, [otThresholdData]);
  const thresholdTotals: any = (!Array.isArray(otThresholdData) && (otThresholdData as any)?.totals) || null;

  // Cost by job data
  const costRows: any[] = useMemo(() => {
    const body: any = costByJobData;
    const raw = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
    // sortCompare sorts null LAST in either direction, so a job whose cost the
    // server has not totalled sinks to the bottom instead of sorting among the
    // genuinely-cheap ones as if it were $0.
    return [...raw].sort((a, b) => sortCompare(parseMoney(rowLabourCost(a)), parseMoney(rowLabourCost(b)), 'desc'));
  }, [costByJobData]);
  const costTotals: any = (!Array.isArray(costByJobData) && (costByJobData as any)?.totals) || null;

  // ── Shop time data ────────────────────────────────────────────────────────
  const shopPreview: ShopPreview | null = shopPreviewData ?? null;
  // Shop rows only: a US hourly hand's FIELD hours come back on this endpoint
  // too (fieldiq-api #1110), and they are not shop time. Every figure below is
  // `shop`, which the API computes over exactly these rows.
  const shopRows: ShopPreviewRow[] = useMemo(
    () => (Array.isArray(shopPreview?.rows) ? shopPreview!.rows.filter((r) => r.workLocation === 'shop') : []),
    [shopPreview],
  );
  const shopSummary: ShopSummary | null = shopPreview?.shop ?? null;

  /**
   * An unpriced row is one whose `amount` is null. Testing the amount rather than
   * the gap keeps the two in step even if a future reason is added server-side
   * that this build has never heard of.
   */
  const shopUnpricedRows = useMemo(() => shopRows.filter((r) => r.amount === null), [shopRows]);

  /**
   * Rows unpriced because somebody DECIDED so, split from rows unpriced because
   * something is missing.
   *
   * Every exempt row is also an unpriced row, so the two sets overlap and the
   * gap-shaped one has to be the difference rather than the whole. Keeping them
   * apart here is what lets the banners, the counts and the table each say the
   * right sentence without re-deriving the distinction three times.
   */
  const shopExemptRows = useMemo(
    () => shopUnpricedRows.filter((r) => isJobPayExemptGap(r.gap)),
    [shopUnpricedRows],
  );
  const shopGapRows = useMemo(
    () => shopUnpricedRows.filter((r) => !isJobPayExemptGap(r.gap)),
    [shopUnpricedRows],
  );

  /**
   * "Nothing priced" and "some priced" now describe the GAP rows only.
   *
   * A period where the only unpriced hands are exempt is not a period with a
   * problem, and the amber "none of it is priced" banner was the loudest thing
   * on the tab. Keyed on `shopGapRows` so a fully-exempt period renders calm.
   */
  const shopAllUnpriced = shopGapRows.length > 0 && shopGapRows.length === shopRows.length;
  const shopSomeUnpriced = shopGapRows.length > 0 && !shopAllUnpriced;

  /**
   * Whether every blocked row is blocked for the SAME reason. When it is — and
   * today, on every tenant, it is `no_shop_rate_configured` — the banner can name
   * one fix. When reasons differ, sending everyone to the same fix would send the
   * worker with an expired effective window to add a row that already exists, so
   * the banner defers to the per-row wording instead.
   */
  const shopOnlyMissingRate = useMemo(() => {
    if (shopGapRows.length === 0) return false;
    return shopGapRows.every((r) => r.gap === 'no_shop_rate_configured');
  }, [shopGapRows]);

  /**
   * Every count on this tab is the API's (`shop`), never derived here. This used
   * to be `rows.length - workersMissingRate - workersJobPayExempt`, which went
   * wrong twice: once when #423 narrowed the missing-rate count (exempt workers
   * were promoted to "priced"), and again when #1110 made a row a worker ×
   * location, so `rows.length` stopped being a worker count at all. A null is an
   * absent figure and renders "—", not a fallback tally.
   */
  const shopWorkerCount = shopSummary?.workerCount ?? null;
  const shopWorkersPriced = shopSummary?.workersPriced ?? null;
  const shopWorkersMissingRate = shopSummary?.workersMissingRate ?? null;
  const shopWorkersJobPayExempt = shopSummary?.workersJobPayExempt ?? null;
  const workersText = (n: number | null) => (n === null ? '— workers' : `${n} worker${n !== 1 ? 's' : ''}`);
  // A worker can hold more than one shop row; name each person once.
  const shopExemptNames = Array.from(new Set(shopExemptRows.map((r) => r.workerName || 'Unnamed worker')));

  const visibleTabs = TIMESHEET_REPORT_TABS.filter((t) => !t.hidden);
  const activeTabLabel = TIMESHEET_REPORT_TABS.find((t) => t.key === activeTab)?.label ?? 'Report';

  /** The panel header every report body shares: eyebrow, then the report's name. */
  const panelHeader = (
    <header className="border-b border-fieldiq-border px-4 py-4">
      <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Timesheet reports</p>
      <h2 className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">{activeTabLabel}</h2>
    </header>
  );

  return (
    // No background and no page padding of its own: it renders inside the
    // Timesheets screen's content section, which owns both. A second one painted
    // a band that stopped at the content.
    <div className="space-y-4">
      {/* ── Period row ──
          Not shown on OT threshold, which is scoped to a calendar month and carries
          its own month control. A pay-period picker left on screen there would be
          a control that visibly changes nothing.

          AND IT REACHES ONLY THESE REPORTS. The status list next door is scoped by
          status and never by pay period (fe#688); the picker lives in this
          component so that stays true by construction rather than by two
          components agreeing to ignore each other. */}
      {activeTab !== 'threshold' && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-fieldiq-muted">Pay period</span>

          {/* Period selector */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              // The name carries the selection. A bare "Pay period" label would
              // replace the on-screen text, and the chosen period is the part a
              // screen reader most needs to hear.
              aria-label={`Pay period: ${activePeriod ? periodLabel(activePeriod) : 'none selected'}`}
              aria-expanded={dropdownOpen}
              className="flex min-w-0 max-w-full items-center gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3.5 py-2 text-sm text-fieldiq-text transition-colors hover:border-fi-amber/60"
            >
              {activePeriod ? periodLabel(activePeriod) : 'Select period…'}
              <ChevronDown size={16} className="text-fieldiq-muted" />
            </button>
            {dropdownOpen && (
              <div
                className="absolute left-0 top-full z-50 mt-1 max-h-[300px] overflow-y-auto rounded-fi-control border border-fieldiq-border bg-fieldiq-card shadow-xl"
                style={{ minWidth: 'min(300px, calc(100vw - 48px))' }}
              >
                {periods.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { setSelectedPeriodId(p.id); setDropdownOpen(false); }}
                    className={`block w-full px-3.5 py-2.5 text-left text-sm text-fieldiq-text transition-colors hover:bg-fieldiq-dark/50 ${
                      p.id === activePeriodId ? 'bg-fi-amber/10' : ''
                    }`}
                  >
                    {periodLabel(p)}
                  </button>
                ))}
                {periods.length === 0 && (
                  <div className="px-3.5 py-3 text-sm text-fieldiq-muted">No pay periods found</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Report tab strip ──
          THE SECOND OF THE PAGE'S TWO TAB LEVELS. The outer strip — under the
          band, in ApprovalQueueScreen — chooses between the timesheet LIST and
          these reports; this one chooses WHICH report. Both are tab strips
          rather than chips because both switch the question being asked rather
          than narrowing one list (see ViewStrip's docblock for that test), and
          neither carries a count, because no endpoint returns one per report and
          a made-up one would be a count of nothing.

          It is the smaller of the two and sits directly above the panel it
          re-titles, so the nesting reads as nesting rather than as two peers. */}
      <div className="flex gap-0 overflow-x-auto border-b border-fieldiq-border" role="group" aria-label="Choose a report">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            aria-pressed={activeTab === t.key}
            /* Same as the view strip above: the active report is marked by the
               amber underline and the weight, not by an ink colour. The accent
               reads 4.36 on the page surface in light, under the floor. */
            className={`relative whitespace-nowrap px-4 py-2.5 text-xs transition-colors ${
              activeTab === t.key
                ? 'font-extrabold text-fieldiq-text'
                : 'font-bold text-fieldiq-muted hover:text-fieldiq-text'
            }`}
          >
            {t.label}
            {activeTab === t.key && (
              <span aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-[2px] bg-fi-amber" />
            )}
          </button>
        ))}
      </div>

      {/* ── Loading ── */}
      {(periodsLoading || isTabLoading) && (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-fieldiq-muted" />
        </div>
      )}

      {/* ── No periods ── */}
      {!periodsLoading && periods.length === 0 && activeTab !== 'threshold' && (
        <div className={PANEL_CLASS}>
          <div className="px-4 py-16 text-center">
            <AlertCircle size={40} className="mx-auto mb-3 text-fieldiq-muted" />
            <div className="text-base text-fieldiq-muted">No pay periods found</div>
          </div>
        </div>
      )}

      {/* One notice for all four tabs — the gate is one gate. Replaces the
          tab body entirely; a refusal has no rows and no totals. */}
      {reportsForbidden && <NoPayReportAccessNotice role={user?.role} />}

      {/* ── TAB 1: Hours breakdown ── */}
      {activeTab === 'hours' && !hoursLoading && activePeriod && !reportsForbidden && (
        <>
          {hoursRows.length > 0 && (
            <div className="flex justify-end">
              <ExportCsvButton
                onClick={() => {
                  // No Gross Pay column: timesheets.gross_pay is unwritten since
                  // api#343. Field pay is a percentage of the ticket, not hourly.
                  // Hours of record and clocked hours as two numeric columns
                  // rather than the on-screen "7.25h (clocked 7.30h)" text, so
                  // the file stays sortable. Clocked is blank when the API sent
                  // null, which it does whenever it equals hours of record.
                  const headers = ['Worker', 'Vertical', 'Hours of Record', 'Clocked Hrs', 'Regular Hrs', 'OT Hrs', 'Double Time Hrs'];
                  const rows = hoursRows.map((r: any) => [
                    `"${r.worker_name || ''}"`,
                    `"${rowVertical(r) ?? ''}"`,
                    r.hours_of_record ?? '',
                    r.clocked_hours ?? '',
                    formatHours(r.regular_hours),
                    formatHours(rowOtHours(r)),
                    formatHours(rowDtHours(r)),
                  ]);
                  downloadCSV(headers, rows, `hours-summary-period-${activePeriod.period_number ?? activePeriodId}.csv`);
                }}
              />
            </div>
          )}

          <section className={`${PANEL_CLASS} border-l-4 border-l-fi-amber`}>
            {panelHeader}
            {hoursRows.length === 0 ? (
              <EmptyReport message="No hours data for this period" />
            ) : (
              /* A min width and a sideways scroll rather than a card stack below
                 md: a second, stacked copy of these rows would put a SECOND set
                 of column headers on the page, and the payload-key suite asserts
                 the page-wide header list exactly. */
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-left">
                  <thead className={THEAD_CLASS}>
                    <tr className={RAIL_NONE}>
                      {['Worker', 'Vertical', 'Hours of Record', 'Regular Hrs', 'OT Hrs', 'Double Time Hrs'].map((h) => (
                        <th key={h} scope="col" className={TH_CLASS}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {hoursRows.map((r: any, i: number) => (
                      <tr key={i} className={`border-t border-fieldiq-border ${RAIL_NONE}`}>
                        <td className={`${TD_CLASS} font-medium whitespace-nowrap`}>{r.worker_name || '—'}</td>
                        <td className={`${TD_CLASS} text-fieldiq-muted`}>{rowVertical(r) ?? '—'}</td>
                        <td className={`${TD_CLASS} whitespace-nowrap`}>{reportHoursText(r.hours_of_record, r.clocked_hours)}</td>
                        <td className={TD_CLASS}>{formatHours(r.regular_hours)}</td>
                        <td className={`${TD_CLASS} ${Number(rowOtHours(r)) > 0 ? 'text-fieldiq-accent-signal' : ''}`}>{formatHours(rowOtHours(r))}</td>
                        <td className={`${TD_CLASS} ${Number(rowDtHours(r)) > 0 ? 'text-fieldiq-text' : ''}`}>{formatHours(rowDtHours(r))}</td>
                      </tr>
                    ))}
                    {/* Totals row — every figure is the API's `totals`. */}
                    <tr className={`border-t-2 border-fieldiq-border bg-fi-amber/5 ${RAIL_NONE}`}>
                      <td className={`${TD_CLASS} font-bold`}>Totals</td>
                      <td className={TD_CLASS} />
                      <td className={`${TD_CLASS} font-bold whitespace-nowrap`}>{reportHoursText(hoursTotals?.hours_of_record, hoursTotals?.clocked_hours)}</td>
                      <td className={`${TD_CLASS} font-bold`}>{formatHours(hoursTotals?.regular_hours)}</td>
                      <td className={`${TD_CLASS} font-bold text-fieldiq-accent-signal`}>{formatHours(hoursTotals?.ot_hours)}</td>
                      <td className={`${TD_CLASS} font-bold text-fieldiq-text`}>{formatHours(hoursTotals?.dt_hours)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {/* ── TAB 2: OT analysis ── */}
      {activeTab === 'ot' && !otLoading && activePeriod && !reportsForbidden && (
        <>
          {/* Summary cards — only when the API sends a summary (it does not today). */}
          {otSummary && (
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              <ReportStatCard label="Total OT Hours" value={formatHours(otSummary.total_ot_hours)} />
              <ReportStatCard
                label="Workers with OT"
                value={otSummary.workers_with_ot == null ? '—' : String(otSummary.workers_with_ot)}
              />
            </div>
          )}

          <section className={`${PANEL_CLASS} border-l-4 border-l-fi-amber`}>
            {panelHeader}
            {otRows.length === 0 ? (
              <EmptyReport message="No OT data for this period" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] border-collapse text-left">
                  <thead className={THEAD_CLASS}>
                    <tr className={RAIL_NONE}>
                      {/* No OT Cost column: it reads timesheets.overtime_pay, unwritten
                          since api#343. OT hours are real; OT dollars are not. */}
                      {['Vertical', 'OT Hours', 'Workers'].map((h) => (
                        <th key={h} scope="col" className={TH_CLASS}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {otRows.map((r: any, i: number) => (
                      <tr key={i} className={`border-t border-fieldiq-border ${RAIL_NONE}`}>
                        <td className={`${TD_CLASS} font-medium`}>{rowVertical(r) ?? '—'}</td>
                        <td className={`${TD_CLASS} text-fieldiq-accent-signal`}>{formatHours(r.ot_hours)}</td>
                        <td className={TD_CLASS}>{r.worker_count ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {/* ── TAB 3: OT threshold ── */}
      {activeTab === 'threshold' && !thresholdLoading && !reportsForbidden && (
        <>
          <div className="space-y-2">
            <div className="text-xs text-fieldiq-muted">
              AB Oilwell Servicing Exception — 191h/month threshold
            </div>
            <div className="flex items-center gap-3">
              <select
                value={thresholdMonth ?? ''}
                onChange={(e) => setThresholdMonth(Number(e.target.value))}
                aria-label="Month"
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2000, i).toLocaleString('en-US', { month: 'long' })}
                  </option>
                ))}
              </select>
              <input
                type="number"
                value={thresholdYear ?? ''}
                onChange={(e) => setThresholdYear(Number(e.target.value))}
                aria-label="Year"
                className="w-[90px] rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text"
              />
            </div>
          </div>

          <section className={`${PANEL_CLASS} border-l-4 border-l-fi-amber`}>
            {panelHeader}
            {thresholdRows.length === 0 ? (
              <EmptyReport
                message={thresholdYear == null || thresholdMonth == null
                  ? 'Pick a month and year'
                  : 'No threshold data for this month'}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-left">
                  <thead className={THEAD_CLASS}>
                    <tr className={RAIL_NONE}>
                      {['Worker', 'Monthly Hours', 'Threshold', 'Hours Remaining', 'Status'].map((h) => (
                        <th key={h} scope="col" className={TH_CLASS}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {thresholdRows.map((r: any, i: number) => {
                      // `Number(x) ?? 0` was dead: Number() returns NaN, never null, so an absent
                      // hours_remaining reached formatHours as NaN and printed 0.0.
                      const remaining = r.hours_remaining;
                      const verdict = thresholdVerdict(remaining);
                      return (
                        <tr key={i} className={`border-t border-l-4 border-fieldiq-border ${verdict.rail}`}>
                          <td className={`${TD_CLASS} font-medium whitespace-nowrap`}>{r.worker_name || '—'}</td>
                          {/* hours_of_record, not monthly_hours: since api#824
                              they are the same figure, and only this key comes
                              with the clocked_hours it is paired against. */}
                          <td className={`${TD_CLASS} whitespace-nowrap`}>{reportHoursText(r.hours_of_record, r.clocked_hours)}</td>
                          <td className={`${TD_CLASS} text-fieldiq-muted`}>191h</td>
                          <td className={`${TD_CLASS} font-semibold`}>{formatHours(remaining)}</td>
                          <td className="px-4 py-3">
                            <ReportBadge label={verdict.label} fill={verdict.fill} />
                          </td>
                        </tr>
                      );
                    })}
                    {/* Totals row — the API's `totals`. Hours only: the route
                        totals no threshold or remaining figure for the month. */}
                    <tr className={`border-t-2 border-fieldiq-border bg-fi-amber/5 ${RAIL_NONE}`}>
                      <td className={`${TD_CLASS} font-bold`}>Totals</td>
                      <td className={`${TD_CLASS} font-bold whitespace-nowrap`}>{reportHoursText(thresholdTotals?.hours_of_record, thresholdTotals?.clocked_hours)}</td>
                      <td className={TD_CLASS} />
                      <td className={TD_CLASS} />
                      <td className={TD_CLASS} />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {/* ── TAB 4: Cost by job ── */}
      {activeTab === 'cost' && !costLoading && activePeriod && !reportsForbidden && (
        <section className={`${PANEL_CLASS} border-l-4 border-l-fi-amber`}>
          {panelHeader}
          {costRows.length === 0 ? (
            <EmptyReport message="No cost data for this period" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] border-collapse text-left">
                <thead className={THEAD_CLASS}>
                  <tr className={RAIL_NONE}>
                    {['Job Name', 'Well ID', 'Workers', 'Hours of Record', 'Total Labour Cost'].map((h) => (
                      <th key={h} scope="col" className={TH_CLASS}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {costRows.map((r: any, i: number) => (
                    <tr key={i} className={`border-t border-fieldiq-border ${RAIL_NONE}`}>
                      <td className={`${TD_CLASS} font-medium`}>{r.job_name || '—'}</td>
                      <td className={`${TD_CLASS} text-fieldiq-muted`}>{rowWell(r) || '—'}</td>
                      <td className={TD_CLASS}>{r.worker_count ?? r.workers ?? '—'}</td>
                      {/* Hours of record, not total_hours: total_hours is the
                          sum of the regular / OT / DT split, a pay category
                          breakdown, and carries no clocked figure beside it. */}
                      <td className={`${TD_CLASS} whitespace-nowrap`}>{reportHoursText(r.hours_of_record, r.clocked_hours)}</td>
                      <td className={`${TD_CLASS} font-semibold text-fieldiq-text`}>{fmtCurrency(rowLabourCost(r))}</td>
                    </tr>
                  ))}
                  {/* Totals row — the API's `totals`. Workers is left blank: the
                      route sends no worker total, and none is added up here. */}
                  <tr className={`border-t-2 border-fieldiq-border bg-fi-amber/5 ${RAIL_NONE}`}>
                    <td className={`${TD_CLASS} font-bold`}>Totals</td>
                    <td className={TD_CLASS} />
                    <td className={TD_CLASS} />
                    <td className={`${TD_CLASS} font-bold whitespace-nowrap`}>{reportHoursText(costTotals?.hours_of_record, costTotals?.clocked_hours)}</td>
                    <td className={`${TD_CLASS} font-bold text-fieldiq-text`}>{fmtCurrency(costTotals?.total_labour_cost)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ── TAB 5: Shop time ── */}
      {activeTab === 'shop' && !periodsLoading && !canViewShopPay && activePeriod && (
        <NoShopPayAccessNotice role={user?.role} />
      )}

      {activeTab === 'shop' && canViewShopPay && !shopLoading && activePeriod && (
        <>
          {/* The server refused while our own check allowed. Since #395 the two
              lists agree exactly, so in steady state this cannot fire — it is kept
              for the window where this bundle is deployed against an API that has
              not taken #395 yet, which is a real transient and not a hypothetical.
              Trust the server, and never let a refusal fall through to "no shop
              hours". */}
          {shopError && isRefusal(shopError) && <NoShopPayAccessNotice role={user?.role} />}

          {/* A refusal is not a failure and a failure is not an empty period.
              All three are distinct and all three are pinned by the tests. */}
          {shopError && !isRefusal(shopError) && (
            <div className={`${PANEL_CLASS} px-6 py-16 text-center`}>
              <AlertCircle size={40} className="mx-auto mb-3 text-fieldiq-text" />
              <div className="mb-1.5 text-base font-semibold text-fieldiq-text">
                Couldn&rsquo;t load shop pay for this period
              </div>
              <div className="text-sm text-fieldiq-muted">
                This is a loading failure, not a report that nobody worked in the shop.
              </div>
            </div>
          )}

          {/* THE FOURTH STATE, AND IT LOOKS EXACTLY LIKE THE FIRST.
              `rows` carries APPROVED shop shifts only. A shop shift still sitting
              in the approval queue is absent from this response entirely — not a
              zero row, not a gap row — so an empty report here is a fact about
              the approval queue at least as often as it is a fact about the yard.
              Saying which is the whole point; the absent-vs-zero rule, one layer
              up from the numbers. */}
          {!shopError && shopRows.length === 0 && (
            <div className={`${PANEL_CLASS} px-6 py-12 text-center`}>
              <Warehouse size={40} className="mx-auto mb-3 text-fieldiq-muted" />
              <div className="mb-1.5 text-base font-semibold text-fieldiq-text">
                No approved shop hours in this period
              </div>
              <div className="mx-auto mb-4 max-w-[560px] text-sm leading-relaxed text-fieldiq-muted">
                Only <strong className="text-fieldiq-text">approved</strong> shop shifts are counted here.
                A shop shift still waiting in the approval queue does not appear on this report at
                all, so check the queue before reading this as &ldquo;nobody worked in the shop&rdquo;.
              </div>
              <button
                onClick={() => navigate('/payroll/approvals')}
                className="fi-action fi-action--compact mx-auto border border-fi-amber text-fieldiq-accent-signal"
              >
                Open the approval queue
                <ArrowRight size={14} />
              </button>
            </div>
          )}

          {!shopError && shopRows.length > 0 && (
            <>
              {/* THE STATE EVERY TENANT IS IN TODAY. `shop` only became a
                  line_item_type in API migration 0336, so no worker anywhere has
                  a Shop rate yet and every row comes back blocked. A screen of
                  em dashes with no explanation reads as broken rather than as
                  unconfigured, so this names the fix and where to apply it. */}
              {shopAllUnpriced && (
                <div className="flex items-start gap-3 rounded-fi-panel border border-fi-amber/40 bg-fi-amber/10 px-4 py-4">
                  <AlertTriangle size={18} className="mt-0.5 shrink-0 text-fieldiq-accent-signal" />
                  <div>
                    <div className="mb-1 text-sm font-bold text-fieldiq-text">
                      {workersText(shopWorkerCount)} logged{' '}
                      {formatHours(shopSummary?.totalHours)} shop hours, and none of it is priced
                    </div>
                    <div className="text-[13px] leading-relaxed text-fieldiq-text">
                      {shopOnlyMissingRate ? (
                        <>
                          Nobody has a <strong>Shop</strong> pay rate configured yet. Set an hourly
                          amount on the Shop row under <strong>Team &rarr; the worker &rarr; Edit Pay
                          Rates</strong>, and these hours will price. The hours themselves are
                          recorded and are not lost.
                        </>
                      ) : (
                        <>
                          The reason differs per worker &mdash; each row below says which, and they
                          need different fixes. Shop rates live under{' '}
                          <strong>Team &rarr; the worker &rarr; Edit Pay Rates</strong>.
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* pricedAmount deliberately EXCLUDES unpriced hours rather than
                  treating them as zero, so a total presented on its own would be
                  honest arithmetic and a misleading sentence. Say what it covers. */}
              {shopSomeUnpriced && (
                <div className="flex items-start gap-3 rounded-fi-panel border border-fi-amber/40 bg-fi-amber/10 px-4 py-4">
                  <AlertTriangle size={18} className="mt-0.5 shrink-0 text-fieldiq-accent-signal" />
                  <div>
                    <div className="mb-1 text-sm font-bold text-fieldiq-text">
                      {fmtCurrency(shopSummary?.pricedAmount)} covers {shopWorkersPriced ?? '—'} of{' '}
                      {workersText(shopWorkerCount)}
                    </div>
                    <div className="text-[13px] leading-relaxed text-fieldiq-text">
                      {formatHours(shopSummary?.unpricedHours)} hours are not priced and are{' '}
                      <strong>not included</strong> in that amount. Each unpriced row says why.
                    </div>
                  </div>
                </div>
              )}

              {/*
                ── EXEMPT WORKERS ARE STATED, NOT HIDDEN ────────────────────
                Dropping them from the tab would make the gap count clean and
                the screen a liar: their hours are real, they are inside
                `totalHours`, and the whole point of the setting is that an
                admin can see it is still the right answer. So this is muted
                rather than amber — a statement, not a warning — and it names
                the people, because "1 worker is exempt" is not something you
                can check and "Chris Salyers is exempt" is.
              */}
              {shopExemptRows.length > 0 && (
                <div className="flex items-start gap-3 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4">
                  <Info size={18} className="mt-0.5 shrink-0 text-fieldiq-muted" />
                  <div>
                    <div className="mb-1 text-sm font-bold text-fieldiq-text">
                      {workersText(shopWorkersJobPayExempt)} not
                      paid from jobs
                    </div>
                    <div className="text-[13px] leading-relaxed text-fieldiq-muted">
                      {shopExemptNames.join(', ')}
                      {' — '}
                      their shop hours are recorded but not priced, on purpose. This is not a
                      missing rate and needs no action. Change it under{' '}
                      <strong>Team &rarr; the worker &rarr; Job pay arrangement</strong> if it is
                      wrong.
                    </div>
                  </div>
                </div>
              )}

              {/* ── Summary cards ── */}
              <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <ReportStatCard label="Shop hours" value={formatHours(shopSummary?.totalHours)} />
                <ReportStatCard label="Unpriced hours" value={formatHours(shopSummary?.unpricedHours)} />
                <ReportStatCard
                  label="Workers with no rate"
                  value={shopWorkersMissingRate === null ? '—' : String(shopWorkersMissingRate)}
                  /**
                   * The caption is what stops this reading as "0 problems".
                   * A zero here with exempt workers on the period means
                   * "nothing to fix AND some people are off job pay", which
                   * is a different state from "nothing to fix"; without the
                   * second sentence an admin cannot tell them apart, and the
                   * setting they would need to audit is invisible.
                   */
                  caption={(shopWorkersJobPayExempt ?? 0) > 0 ? `${shopWorkersJobPayExempt} more not paid from jobs` : null}
                />
                <ReportStatCard
                  label="Priced so far"
                  /**
                   * `pricedAmount` really is 0 when nothing priced, and that
                   * zero is arithmetically honest — but a zero under a money
                   * heading reads as "we looked and you earned nothing", which
                   * is the exact sentence this whole feature refuses to say.
                   * With nothing priced there is no amount to report, so the
                   * em dash is the truthful render and the caption carries the
                   * count.
                   */
                  value={!shopWorkersPriced ? '—' : fmtCurrency(shopSummary?.pricedAmount)}
                  caption={`${shopWorkersPriced ?? '—'} of ${workersText(shopWorkerCount)} priced`}
                />
              </div>

              <div className="flex justify-end">
                <ExportCsvButton
                  onClick={() => {
                    const headers = ['Worker', 'Shifts', 'Shop Hours', 'Hourly Rate', 'OT Eligible', 'Amount', 'Not priced because'];
                    const rows = shopRows.map((r) => [
                      `"${r.workerName || ''}"`,
                      String(r.shiftCount ?? ''),
                      r.hours === null || r.hours === undefined ? '' : String(r.hours),
                      // An unpriced rate and amount leave the CELL EMPTY. A zero
                      // in a payroll CSV bound for an accountant is a number they
                      // will sum; a blank is a gap they will ask about.
                      r.hourlyRate === null || r.hourlyRate === undefined ? '' : r.hourlyRate.toFixed(2),
                      shopOtLabel(r.overtimeEligible, ''),
                      r.amount === null || r.amount === undefined ? '' : r.amount.toFixed(2),
                      r.amount === null ? `"${shopGapMessage(r.gap, r.gapMessage).replace(/"/g, "'")}"` : '',
                    ]);
                    downloadCSV(headers, rows, `shop-time-period-${activePeriod.period_number ?? activePeriodId}.csv`);
                  }}
                />
              </div>

              <section className={`${PANEL_CLASS} border-l-4 border-l-fi-amber`}>
                {panelHeader}
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[980px] border-collapse text-left">
                    <thead className={THEAD_CLASS}>
                      <tr className={RAIL_NONE}>
                        {['Worker', 'Shifts', 'Shop Hours', 'Hourly Rate', 'OT', 'Amount', 'Why'].map((h) => (
                          <th key={h} scope="col" className={TH_CLASS}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shopRows.map((r) => {
                        /* Priced, blocked, or deliberately exempt — three
                           states, three rails. An exemption is already
                           actioned, so it takes the neutral steel rather than
                           joining the amber queue of things to fix. */
                        const rail = r.amount !== null
                          ? 'border-l-fi-green'
                          : isJobPayExemptGap(r.gap) ? 'border-l-fi-steel' : 'border-l-fi-amber';
                        return (
                          <tr key={shopRowKey(r)} className={`border-t border-l-4 border-fieldiq-border ${rail}`}>
                            <td className={`${TD_CLASS} font-medium`}>{r.workerName || '—'}</td>
                            <td className={`${TD_CLASS} text-fieldiq-muted`}>{r.shiftCount ?? '—'}</td>
                            <td className={TD_CLASS}>{formatHours(r.hours)}</td>
                            {/* fmtCurrency and fmtHours reject null by identity
                                before coercing, so an absent rate or amount is an
                                em dash. No `?? 0` anywhere on this row. */}
                            <td className={TD_CLASS}>{fmtCurrency(r.hourlyRate)}</td>
                            <td
                              data-testid={`shop-ot-${r.userId}`}
                              className={`${TD_CLASS} ${r.overtimeEligible === true ? 'text-fieldiq-text' : 'text-fieldiq-muted'}`}
                            >
                              {shopOtLabel(r.overtimeEligible, '—')}
                            </td>
                            <td className={`${TD_CLASS} font-semibold ${r.amount === null ? 'text-fieldiq-muted' : 'text-fieldiq-text'}`}>
                              {fmtCurrency(r.amount)}
                            </td>
                            <td className={`${TD_CLASS} max-w-[420px] whitespace-normal [font-variant-numeric:normal]`}>
                              {r.amount === null ? (
                                <span
                                  /*
                                    Amber says "act on this". An exemption is
                                    already actioned, so it renders in the
                                    muted body colour: the sentence is still
                                    there and still says why, but the row does
                                    not join the queue of things to fix.
                                  */
                                  className={`text-[13px] leading-normal ${isJobPayExemptGap(r.gap) ? 'text-fieldiq-muted' : 'text-fieldiq-accent-signal'}`}
                                >
                                  {shopGapMessage(r.gap, r.gapMessage)}
                                </span>
                              ) : (
                                // Not an em dash: this row has no gap, which is a
                                // different fact from a value being absent.
                                <span className="text-[13px] text-fieldiq-text">Priced</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-fieldiq-border px-4 py-3 text-xs leading-relaxed text-fieldiq-muted">
                  Approved shop shifts only. The rate is resolved as of the period start, so a rate
                  change part-way through a period is not modelled here.
                </div>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}
